import * as React from 'react';
import { AccentButton } from '../AccentButton';
import { AccentPdfViewer } from '../AccentPdfViewer';
import { buildPdfDocumentDefinition, DOCUMENT_OUTPUT, toParamDeclarations } from '../customizations/pdfDocumentDefinition';
import { CustomParamsCtrl } from '../documents/custom-reports/CustomDocumentParams';
import { postForBlob } from '../../services/DataService';
import { accentUtils, t } from '../../services/HelperService';

const safeString = (v) => (v ?? '').toString();

/** The document script of the session: the output named 'document' (case-insensitive). */
export const readPdfDocumentOutput = (outputs) => {
  const list = Array.isArray(outputs) ? outputs : [];
  return list.find((o) => safeString(o?.name).trim().toLowerCase() === DOCUMENT_OUTPUT) ?? null;
};

/** The session's non-blank queries as definition queries[] entries ({ name, query }). */
export const readPdfDocumentQueries = (queries) => (Array.isArray(queries) ? queries : [])
  .filter((q) => safeString(q?.name).trim() && safeString(q?.query).trim())
  .map((q) => ({ name: safeString(q.name).trim(), query: safeString(q.query) }));

/** The session's parameter declarations as the definition params[] array ([] for blank/malformed). */
export const readPdfDocumentParams = (documentParamsJson) => {
  const text = safeString(documentParamsJson).trim();
  if (!text) return [];
  try {
    const parsed = JSON.parse(text);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/**
 * Preview tab of the pdfDocument assistant: renders the session's document script for the
 * editor's test job through the SAME path the editor's Preview button uses
 * (DownloadController.TestPdfScriptReport, in-process pdfscript engine), so what the user sees
 * here is exactly what "Use Script" + Preview would show. The definition rendered is the host's
 * base definition (no outputs) with the SESSION's queries and parameter declarations — the
 * assistant may have changed either this turn — and the job / print setting / signing choice
 * ride the host context the editor snapshots for every send.
 *
 * Renders automatically when the script, queries or parameters change once the chat turn is
 * over (busy false) — the server has already test-rendered the candidate during the turn, so
 * this is the user's copy of that result, not a second validation — and on demand through the
 * Render button. A render failure shows inline and is pushed to the transcript (onDefect) so
 * the next prompt can quote it, and reported to the dialog (onOutputRendered) so the output's
 * test badge and the Use Script guard see it - the render IS this host's test, and a hand
 * edit of the script is only ever tested here.
 */
export const PdfDocumentPreview = ({ outputs, queries, documentParamsJson, busy, hostContext, onDefect, onOutputRendered }) => {
  const output = readPdfDocumentOutput(outputs);
  const script = safeString(output?.script);
  const recordID = Number(hostContext?.recordID) || 0;
  const customizationID = Number(hostContext?.customizationID) || 0;
  // One string per renderable state so "changed since this render" covers all three artifacts.
  const sessionQueries = React.useMemo(() => readPdfDocumentQueries(queries), [queries]);
  const sessionParams = React.useMemo(() => readPdfDocumentParams(documentParamsJson), [documentParamsJson]);
  // the declarations as the print dialog will render them, and the values chosen here to preview with
  // (ctx.params): a parameter the assistant just declared can be toggled live, not only seen at its default
  const paramDeclarations = React.useMemo(() => toParamDeclarations(sessionParams), [sessionParams]);
  const [testParams, setTestParams] = React.useState({});
  const renderKey = React.useMemo(
    () => JSON.stringify({ script, queries: sessionQueries, params: sessionParams, values: testParams }),
    [script, sessionQueries, sessionParams, testParams],
  );

  const [url, setUrl] = React.useState(null);
  const [fileName, setFileName] = React.useState('');
  const [rendering, setRendering] = React.useState(false);
  const [error, setError] = React.useState('');
  const [renderedKey, setRenderedKey] = React.useState(null);
  const [height, setHeight] = React.useState(500);
  // A conversion session opens on a script that is known to read the SOURCE type's contract; rendering
  // it against this type's record can only fail, and that failure would land in the transcript as if
  // the assistant had produced it. The opening script is therefore never auto-rendered - whatever
  // happens to the parameter values or queries meanwhile (the parameter row seeds its defaults right
  // after mount, which changes the render key) - only an artifact the assistant changed is; Render
  // still works on demand and, once used, re-enables auto-rendering.
  const [skippedNote, setSkippedNote] = React.useState('');
  const convertFrom = safeString(hostContext?.convertFromType);
  const openingScriptRef = React.useRef(convertFrom && !accentUtils.isEmpty(script) ? script : null);

  const urlRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const onDefectRef = React.useRef(onDefect);
  onDefectRef.current = onDefect;
  const onOutputRenderedRef = React.useRef(onOutputRendered);
  onOutputRenderedRef.current = onOutputRendered;
  const outputName = safeString(output?.name).trim() || DOCUMENT_OUTPUT;

  // object URLs are released when replaced and when the tab unmounts
  React.useEffect(() => () => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
  }, []);

  // the viewer needs a pixel height: track the size of its own (flex-sized) slot, whatever the
  // toolbar and parameter rows above it take
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const h = Math.floor(entries[0]?.contentRect?.height ?? 0);
      if (h > 100) setHeight(h);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const render = React.useCallback(async (e) => {
    try {
      setError('');
      setSkippedNote('');
      openingScriptRef.current = null; // a deliberate render ends the skip
      if (accentUtils.isEmpty(script)) {
        setError(t('The session has no document script yet.'));
        return;
      }
      if (!(recordID > 0)) {
        setError(t('Pick a test job in the editor before opening the assistant to render previews.'));
        return;
      }
      setRendering(true);
      const base = hostContext?.definitionBase ? JSON.stringify(hostContext.definitionBase) : null;
      const result = await postForBlob('/Download/TestPdfScriptReport', {
        CustomizationID: customizationID,
        Definition: buildPdfDocumentDefinition({ existing: base, script, queries: sessionQueries, params: sessionParams }),
        RecordID: recordID,
        PrintSettingID: hostContext?.printSettingID ?? null,
        UseDigitalSignature: hostContext?.digital === true,
        CustomParams: Object.keys(testParams).length > 0 ? testParams : null,
      });
      if (urlRef.current && urlRef.current !== result.url) URL.revokeObjectURL(urlRef.current);
      urlRef.current = result.url;
      setUrl(result.url);
      setFileName(result.fileName ?? '');
      setRenderedKey(renderKey);
      onOutputRenderedRef.current?.(outputName, true, '');
    } catch (ex) {
      const message = safeString(ex?.message) || t('Preview failed.');
      setError(message);
      setRenderedKey(renderKey);
      onDefectRef.current?.(`${t('Document render failed')}: ${message}`);
      onOutputRenderedRef.current?.(outputName, false, `${t('Render failed')}: ${message}`);
    } finally {
      setRendering(false);
      e?.promise?.();
    }
  }, [customizationID, hostContext, outputName, recordID, renderKey, script, sessionParams, sessionQueries, testParams]);

  // auto-render changed artifacts once the turn is over
  React.useEffect(() => {
    if (busy || rendering) return;
    if (accentUtils.isEmpty(script) || !(recordID > 0)) return;
    if (renderedKey === renderKey) return;
    if (openingScriptRef.current !== null && script === openingScriptRef.current) {
      setRenderedKey(renderKey);
      setSkippedNote(t('This script still reads the {{from}} contract, so it was not rendered; the preview renders once the conversion lands.', { from: convertFrom }));
      return;
    }
    openingScriptRef.current = null;
    setSkippedNote('');
    render();
  }, [busy, convertFrom, recordID, render, renderedKey, renderKey, rendering, script]);

  const stale = !!url && renderedKey !== renderKey;

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5, flexWrap: 'wrap' }}>
        <AccentButton tagName="pdfAssistantRender" disabled={rendering || busy || accentUtils.isEmpty(script)} onClick={render}
          tooltip={t('Render the current document script against the test job. Nothing is saved by a preview.')}>
          {rendering ? t('Rendering...') : t('Render')}
        </AccentButton>
        <span style={{ color: '#6c757d' }}>
          {recordID > 0
            ? `${t('Test job')}: ${safeString(hostContext?.recordReference) || recordID}`
            : t('No test job selected')}
          {hostContext?.digital === true ? ` \u00b7 ${t('Digital signing')}` : ''}
          {sessionQueries.length > 0 ? ` \u00b7 ${t('{{count}} query(ies)', { count: sessionQueries.length })}` : ''}
          {sessionParams.length > 0 ? ` \u00b7 ${t('{{count}} parameter(s)', { count: sessionParams.length })}` : ''}
          {stale ? ` \u00b7 ${t('The document changed since this render.')}` : ''}
        </span>
        {url && (
          <a href={url} download={fileName || 'preview.pdf'} style={{ fontSize: 12, marginLeft: 'auto' }}>{t('Download')}</a>
        )}
      </div>
      {paramDeclarations.length > 0 && (
        <div style={{ padding: '0 4px' }} title={t('Print-time parameters (ctx.params) for the preview; a change re-renders once the turn is over.')}>
          <CustomParamsCtrl
            declarations={paramDeclarations}
            defaultCustomParamValues={testParams}
            onCustomParamsChange={setTestParams}
          />
        </div>
      )}
      {error && (
        <div style={{ color: '#b02a37', fontSize: 12.5, whiteSpace: 'pre-wrap', background: '#fff5f5', borderRadius: 4, padding: '6px 8px' }}>{error}</div>
      )}
      <div ref={containerRef} style={{ flex: 1, minHeight: 0, overflow: 'auto', background: '#f1f3f5', borderRadius: 4 }}>
        {url ? (
          <AccentPdfViewer document={url} height={height} />
        ) : (
          <div style={{ padding: 16, color: '#6c757d', fontSize: 13 }} data-tagname={skippedNote ? 'pdfPreviewSkipped' : undefined}>
            {rendering
              ? t('Rendering...')
              : skippedNote || (accentUtils.isEmpty(script)
                ? t('Ask the assistant for a document and the rendered PDF appears here.')
                : (recordID > 0 ? t('Press Render to see the document.') : t('Pick a test job in the editor to render previews.')))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PdfDocumentPreview;
